import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  ClipboardCheck, Plus, Pencil, Trash2, Building2, Clock,
  Download, Upload, Users, Settings2, ArrowRight, AlertTriangle,
} from 'lucide-react';

const DEPARTMENTS = ['Service', 'Kitchen', 'Bar', 'Housekeeping', 'Room Lobby', 'Room'];

// ITER307 · Valid role keys — mirrors backend core.VALID_ROLES so the
// item-level "assign to" dropdown can offer role-scoped assignment.
const VALID_ROLES = [
  'admin', 'area_manager', 'manager', 'supervisor', 'super_admin',
  'production_head', 'chef', 'cashier', 'store_manager', 'accountant',
  'maintenance',
];

// Human-friendly label for a role key.
function roleLabel(role) {
  return role
    .split('_')
    .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
    .join(' ');
}

// ITER307 · Compact chip showing where an item's failures will route.
function RoutingBadge({ failureAssignTo }) {
  if (!failureAssignTo || !failureAssignTo.value) {
    return (
      <Badge
        variant="outline"
        className="border-[#E2E8DF] text-[#5C6B64] text-[10px] px-1.5"
        data-testid="routing-badge-default"
      >
        <ArrowRight className="w-3 h-3 mr-1 opacity-60" />
        rules
      </Badge>
    );
  }
  const label = failureAssignTo.type === 'role'
    ? roleLabel(failureAssignTo.value)
    : failureAssignTo.value;
  return (
    <Badge
      className="bg-[color:var(--color-primary)]/10 text-[color:var(--color-primary)] border border-[color:var(--color-primary)]/30 text-[10px] px-1.5"
      data-testid={`routing-badge-${failureAssignTo.type}`}
    >
      <ArrowRight className="w-3 h-3 mr-1" />
      {label}
    </Badge>
  );
}

const NO_OVERRIDE = '__none__';    // Select's sentinel for the "rules" default
const ASSIGN_DEPT_PREFIX = 'dept::';
const ASSIGN_ROLE_PREFIX = 'role::';

// Encode / decode `failure_assign_to` as a single Select value.
function encodeAssign(fat) {
  if (!fat || !fat.value) return NO_OVERRIDE;
  return `${fat.type === 'role' ? ASSIGN_ROLE_PREFIX : ASSIGN_DEPT_PREFIX}${fat.value}`;
}
function decodeAssign(v) {
  if (!v || v === NO_OVERRIDE) return null;
  if (v.startsWith(ASSIGN_ROLE_PREFIX))
    return { type: 'role', value: v.slice(ASSIGN_ROLE_PREFIX.length) };
  if (v.startsWith(ASSIGN_DEPT_PREFIX))
    return { type: 'department', value: v.slice(ASSIGN_DEPT_PREFIX.length) };
  return null;
}

export default function AdminChecklist() {
  const { getHeaders, API } = useAuth();
  const [tab, setTab] = useState('master');
  const [items, setItems] = useState([]);
  const [outlets, setOutlets] = useState([]);
  const [departments, setDepartments] = useState(DEPARTMENTS);   // resolved from API
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    department: '', area: '', sub_area: '', task_name: '', is_critical: false,
    failure_assign_to: null,
  });
  const [saving, setSaving] = useState(false);
  const [filterDept, setFilterDept] = useState('all');
  const [selectedOutlet, setSelectedOutlet] = useState('');
  const [deadlines, setDeadlines] = useState({});
  const [deadlineEditing, setDeadlineEditing] = useState(false);

  // ITER307 · Bulk selection state
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkAssignOpen, setBulkAssignOpen] = useState(false);
  const [bulkAssignVal, setBulkAssignVal] = useState(NO_OVERRIDE);
  const [bulkSaving, setBulkSaving] = useState(false);

  // ITER307 · Excel import state
  const [importOpen, setImportOpen] = useState(false);
  const [importPreview, setImportPreview] = useState(null);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const fileInputRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [itemsRes, outletsRes, dlRes, deptRes] = await Promise.all([
        axios.get(`${API}/admin/checklist/master`, { headers: getHeaders() }),
        axios.get(`${API}/admin/outlets`, { headers: getHeaders() }),
        axios.get(`${API}/admin/checklist/deadlines`, { headers: getHeaders() }),
        axios.get(`${API}/admin/departments`, { headers: getHeaders() }).catch(() => ({ data: { departments: DEPARTMENTS } })),
      ]);
      setItems(itemsRes.data);
      setOutlets(outletsRes.data.filter((o) => o.status !== 'inactive'));
      setDeadlines(dlRes.data);
      // ITER307 · Union the API-provided department list with the
      // checklist-owned DEPARTMENTS constant so the "assign to" dropdown
      // always shows Room / Room Lobby / Housekeeping etc. even if the
      // tenant-wide /admin/departments endpoint (which is ticket-centric)
      // omits them.
      setDepartments(
        Array.from(new Set([...DEPARTMENTS, ...(deptRes.data?.departments || [])]))
      );
      if (!selectedOutlet && outletsRes.data.length > 0) {
        setSelectedOutlet(outletsRes.data[0].id);
      }
    } catch (err) { console.error('Failed to load checklist data:', err); toast.error('Failed to load data'); }
    finally { setLoading(false); }
  }, [API, getHeaders, selectedOutlet]);

  useEffect(() => { load(); }, [load]);

  const openAdd = () => {
    setEditing(null);
    setForm({ department: '', area: '', sub_area: '', task_name: '',
              is_critical: false, failure_assign_to: null });
    setDialogOpen(true);
  };
  const openEdit = (item) => {
    setEditing(item);
    setForm({
      department: item.department, area: item.area || '', sub_area: item.sub_area || '',
      task_name: item.task_name, is_critical: item.is_critical,
      failure_assign_to: item.failure_assign_to || null,
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.task_name.trim() || !form.department) { toast.error('Department and task name are required'); return; }
    setSaving(true);
    try {
      const payload = { ...form };
      if (editing) {
        await axios.put(`${API}/admin/checklist/master/${editing.id}`, payload, { headers: getHeaders() });
        toast.success('Item updated');
      } else {
        await axios.post(`${API}/admin/checklist/master`, payload, { headers: getHeaders() });
        toast.success('Item created');
      }
      setDialogOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this checklist item?')) return;
    try {
      await axios.delete(`${API}/admin/checklist/master/${id}`, { headers: getHeaders() });
      toast.success('Item deleted');
      load();
    } catch { toast.error('Failed to delete'); }
  };

  const toggleOutletAssign = async (itemId, outletId, currentlyAssigned) => {
    try {
      const item = items.find((i) => i.id === itemId);
      if (!item) return;
      const newOutlets = currentlyAssigned
        ? item.assigned_outlets.filter((o) => o !== outletId)
        : [...(item.assigned_outlets || []), outletId];
      await axios.put(`${API}/admin/checklist/master/${itemId}/assign`, { outlet_ids: newOutlets }, { headers: getHeaders() });
      setItems((prev) => prev.map((i) => (i.id === itemId ? { ...i, assigned_outlets: newOutlets } : i)));
    } catch { toast.error('Failed to update assignment'); }
  };

  const bulkToggle = async (deptItems, outletId, assign) => {
    const ids = deptItems.map((i) => i.id).join(',');
    try {
      await axios.put(`${API}/admin/checklist/bulk-assign?outlet_id=${outletId}&item_ids=${ids}&action=${assign ? 'add' : 'remove'}`, {}, { headers: getHeaders() });
      toast.success(assign ? 'Assigned all' : 'Unassigned all');
      load();
    } catch { toast.error('Failed to update'); }
  };

  const saveDeadlines = async () => {
    try {
      await axios.put(`${API}/admin/checklist/deadlines`, deadlines, { headers: getHeaders() });
      toast.success('Deadlines saved');
      setDeadlineEditing(false);
    } catch { toast.error('Failed to save deadlines'); }
  };

  // ITER307 · Selection helpers
  const toggleSelectOne = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const clearSelection = () => setSelectedIds(new Set());
  const selectAllFiltered = (rows) => {
    setSelectedIds(new Set(rows.map((r) => r.id)));
  };

  const doBulkAssign = async () => {
    if (selectedIds.size === 0) return;
    setBulkSaving(true);
    try {
      const payload = {
        item_ids: Array.from(selectedIds),
        failure_assign_to: decodeAssign(bulkAssignVal),
      };
      const res = await axios.patch(`${API}/admin/checklist/master/bulk-failure-assign`,
                                     payload, { headers: getHeaders() });
      toast.success(`Updated ${res.data.updated} item${res.data.updated === 1 ? '' : 's'}`);
      setBulkAssignOpen(false);
      clearSelection();
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Bulk update failed');
    } finally { setBulkSaving(false); }
  };

  // ITER307 · Excel import handlers
  const downloadTemplate = async () => {
    try {
      const res = await axios.get(`${API}/admin/checklist/import/template`,
        { headers: getHeaders(), responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url; a.download = 'checklist_import_template.xlsx';
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Template downloaded');
    } catch { toast.error('Failed to download template'); }
  };

  const openImportDialog = () => {
    setImportPreview(null);
    setImportFile(null);
    setImportOpen(true);
    // reset file input so the same file can be re-selected
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const onImportFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImportFile(file);
    setPreviewLoading(true);
    setImportPreview(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/admin/checklist/import/preview`, fd,
        { headers: { ...getHeaders(), 'Content-Type': 'multipart/form-data' } });
      setImportPreview(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Preview failed');
    } finally { setPreviewLoading(false); }
  };

  const commitImport = async () => {
    if (!importFile) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append('file', importFile);
      const res = await axios.post(`${API}/admin/checklist/import/commit`, fd,
        { headers: { ...getHeaders(), 'Content-Type': 'multipart/form-data' } });
      const s = res.data.summary;
      toast.success(
        `${res.data.created} created · ${s.duplicate} skipped (duplicates) · ${s.invalid} rejected`
      );
      setImportOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Import failed');
    } finally { setImporting(false); }
  };

  const filtered = filterDept === 'all' ? items : items.filter((i) => i.department === filterDept);
  const grouped = filtered.reduce((acc, i) => {
    const key = `${i.department} > ${i.area || 'General'} > ${i.sub_area || 'General'}`;
    if (!acc[key]) acc[key] = [];
    acc[key].push(i);
    return acc;
  }, {});

  const selectionCount = selectedIds.size;

  return (
    <div data-testid="admin-checklist-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <div>
          <h2 className="text-lg sm:text-xl tracking-tight font-semibold text-[#1A2421]">Checklist Management</h2>
          <p className="text-sm text-[#5C6B64] mt-1">{items.length} items across {new Set(items.map((i) => i.department)).size} departments</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" onClick={downloadTemplate}
                   className="border-[#E2E8DF] text-[#1A2421]" data-testid="download-template-btn">
            <Download className="w-4 h-4 mr-1.5" /> Download Template
          </Button>
          <Button variant="outline" onClick={openImportDialog}
                   className="border-[#E2E8DF] text-[#1A2421]" data-testid="upload-items-btn">
            <Upload className="w-4 h-4 mr-1.5" /> Upload Checklist Items
          </Button>
          <Button onClick={openAdd}
                   className="bg-[color:var(--color-primary)] hover:bg-[color:var(--color-primary-hover)] text-white"
                   data-testid="add-checklist-btn">
            <Plus className="w-4 h-4 mr-1.5" /> Add Item
          </Button>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-[#F3F5F2] mb-6">
          <TabsTrigger value="master" className="data-[state=active]:bg-[color:var(--color-primary)] data-[state=active]:text-white" data-testid="master-tab">Master List</TabsTrigger>
          <TabsTrigger value="assign" className="data-[state=active]:bg-[color:var(--color-primary)] data-[state=active]:text-white" data-testid="assign-tab">Outlet Assignment</TabsTrigger>
          <TabsTrigger value="deadlines" className="data-[state=active]:bg-[color:var(--color-primary)] data-[state=active]:text-white" data-testid="deadlines-tab">Deadlines</TabsTrigger>
        </TabsList>

        <TabsContent value="master">
          <div className="flex items-center gap-3 mb-4 flex-wrap">
            <Select value={filterDept} onValueChange={setFilterDept}>
              <SelectTrigger className="w-48 h-9 border-[#E2E8DF]" data-testid="dept-filter">
                <SelectValue placeholder="Filter by department" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Departments</SelectItem>
                {DEPARTMENTS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
              </SelectContent>
            </Select>
            <span className="text-sm text-[#5C6B64]">{filtered.length} items</span>

            {/* ITER307 · Bulk-action toolbar */}
            <div className="flex-1" />
            {selectionCount > 0 ? (
              <div className="flex items-center gap-2 bg-[color:var(--color-primary)]/10 border border-[color:var(--color-primary)]/30 rounded-lg px-3 py-1.5"
                   data-testid="bulk-toolbar">
                <span className="text-xs font-semibold text-[color:var(--color-primary)]">
                  {selectionCount} selected
                </span>
                <Button size="sm" variant="outline"
                        onClick={() => { setBulkAssignVal(NO_OVERRIDE); setBulkAssignOpen(true); }}
                        className="h-7 border-[color:var(--color-primary)]/40 text-[color:var(--color-primary)]"
                        data-testid="bulk-assign-btn">
                  <Users className="w-3 h-3 mr-1" /> Set failure assignment
                </Button>
                <Button size="sm" variant="ghost"
                        onClick={clearSelection}
                        className="h-7 text-[#5C6B64]"
                        data-testid="bulk-clear-btn">
                  Clear
                </Button>
              </div>
            ) : filtered.length > 0 && (
              <Button size="sm" variant="ghost"
                      onClick={() => selectAllFiltered(filtered)}
                      className="h-7 text-[#5C6B64] text-xs"
                      data-testid="select-all-filtered-btn">
                Select all {filtered.length}
              </Button>
            )}
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20"><div className="animate-spin w-8 h-8 border-2 border-[color:var(--color-primary)] border-t-transparent rounded-full" /></div>
          ) : (
            <div className="space-y-4">
              {Object.entries(grouped).map(([group, groupItems]) => (
                <Card key={group} className="border-[#E2E8DF] shadow-sm">
                  <CardHeader className="py-3 px-4 bg-[#F9F9F8] border-b border-[#E2E8DF]">
                    <CardTitle className="text-sm font-medium text-[#5C6B64]">{group} ({groupItems.length})</CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    {groupItems.map((item, i) => {
                      const selected = selectedIds.has(item.id);
                      return (
                      <div key={item.id}
                           className={`flex items-center gap-3 px-4 py-2.5 ${i > 0 ? 'border-t border-[#E2E8DF]' : ''} ${selected ? 'bg-[color:var(--color-primary)]/[0.04]' : ''}`}
                           data-testid={`checklist-item-${item.id}`}>
                        <Checkbox
                          checked={selected}
                          onCheckedChange={() => toggleSelectOne(item.id)}
                          data-testid={`select-item-${item.id}`}
                        />
                        <div className="flex-1 min-w-0 mr-3">
                          <p className="text-sm text-[#1A2421] truncate">{item.task_name}</p>
                          <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                            {item.is_critical && <Badge className="bg-[#C45A41]/10 text-[#C45A41] border-[#C45A41]/20 text-[10px] px-1.5">Critical</Badge>}
                            <RoutingBadge failureAssignTo={item.failure_assign_to} />
                            <span className="text-[10px] text-[#5C6B64]">{item.assigned_outlets?.length || 0} outlets</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          <Button variant="ghost" size="sm" onClick={() => openEdit(item)} className="text-[#5C6B64] hover:text-[color:var(--color-primary)]" data-testid={`edit-item-${item.id}`}>
                            <Pencil className="w-3.5 h-3.5" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDelete(item.id)} className="text-[#C45A41] hover:text-[#C45A41]" data-testid={`delete-item-${item.id}`}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </div>
                    );})}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="assign">
          <div className="flex items-center gap-3 mb-4">
            <Select value={selectedOutlet} onValueChange={setSelectedOutlet}>
              <SelectTrigger className="w-56 h-9 border-[#E2E8DF]" data-testid="outlet-assign-select">
                <SelectValue placeholder="Select outlet" />
              </SelectTrigger>
              <SelectContent>
                {outlets.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {selectedOutlet && (
            <div className="space-y-4">
              {DEPARTMENTS.map((dept) => {
                const deptItems = items.filter((i) => i.department === dept);
                if (deptItems.length === 0) return null;
                const assignedCount = deptItems.filter((i) => i.assigned_outlets?.includes(selectedOutlet)).length;
                const allAssigned = assignedCount === deptItems.length;
                return (
                  <Card key={dept} className="border-[#E2E8DF] shadow-sm">
                    <CardHeader className="py-3 px-4 bg-[#F9F9F8] border-b border-[#E2E8DF]">
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-sm font-medium text-[#1A2421]">
                          {dept} <span className="text-[#5C6B64] font-normal">({assignedCount}/{deptItems.length})</span>
                        </CardTitle>
                        <Button variant="outline" size="sm" onClick={() => bulkToggle(deptItems, selectedOutlet, !allAssigned)}
                          className="text-xs border-[#E2E8DF]" data-testid={`bulk-toggle-${dept}`}>
                          {allAssigned ? 'Unassign All' : 'Assign All'}
                        </Button>
                      </div>
                    </CardHeader>
                    <CardContent className="p-0">
                      {deptItems.map((item, i) => {
                        const assigned = item.assigned_outlets?.includes(selectedOutlet);
                        return (
                          <div key={item.id} className={`flex items-center gap-3 px-4 py-2 ${i > 0 ? 'border-t border-[#E2E8DF]' : ''}`}>
                            <Checkbox checked={assigned} onCheckedChange={() => toggleOutletAssign(item.id, selectedOutlet, assigned)}
                              data-testid={`assign-check-${item.id}`} />
                            <span className={`text-sm ${assigned ? 'text-[#1A2421]' : 'text-[#5C6B64]'}`}>{item.task_name}</span>
                            {item.is_critical && <Badge className="bg-[#C45A41]/10 text-[#C45A41] border-[#C45A41]/20 text-[10px] px-1.5 ml-auto">Critical</Badge>}
                          </div>
                        );
                      })}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        <TabsContent value="deadlines">
          <Card className="border-[#E2E8DF] shadow-sm max-w-lg">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base text-[#1A2421] flex items-center gap-2"><Clock className="w-4 h-4" /> Submission Deadlines</CardTitle>
                {!deadlineEditing && (
                  <Button variant="outline" size="sm" onClick={() => setDeadlineEditing(true)} className="border-[#E2E8DF]" data-testid="edit-deadlines-btn">Edit</Button>
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              {DEPARTMENTS.map((dept) => (
                <div key={dept} className="flex items-center justify-between">
                  <span className="text-sm text-[#1A2421]">{dept}</span>
                  {deadlineEditing ? (
                    <Input type="time" value={deadlines[dept] || ''} onChange={(e) => setDeadlines((prev) => ({ ...prev, [dept]: e.target.value }))}
                      className="w-32 h-9 border-[#E2E8DF]" data-testid={`deadline-input-${dept}`} />
                  ) : (
                    <Badge variant="outline" className="border-[#E2E8DF] text-[#5C6B64]" data-testid={`deadline-display-${dept}`}>
                      {deadlines[dept] || '—'}
                    </Badge>
                  )}
                </div>
              ))}
              {deadlineEditing && (
                <div className="flex gap-2 pt-2">
                  <Button size="sm" onClick={saveDeadlines} className="bg-[color:var(--color-primary)] hover:bg-[color:var(--color-primary-hover)] text-white" data-testid="save-deadlines-btn">Save</Button>
                  <Button variant="outline" size="sm" onClick={() => setDeadlineEditing(false)} className="border-[#E2E8DF]">Cancel</Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Add / Edit item dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="checklist-item-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#1A2421]">{editing ? 'Edit Item' : 'Add Checklist Item'}</DialogTitle>
            <DialogDescription className="text-[#5C6B64]">Define the checklist task details.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label className="text-sm text-[#5C6B64]">Department <span className="text-[#C45A41]">*</span></Label>
              <Select value={form.department} onValueChange={(v) => setForm((p) => ({ ...p, department: v }))}>
                <SelectTrigger className="h-11 border-[#E2E8DF]" data-testid="item-dept-select"><SelectValue placeholder="Select department" /></SelectTrigger>
                <SelectContent>{DEPARTMENTS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-sm text-[#5C6B64]">Area</Label>
                <Input value={form.area} onChange={(e) => setForm((p) => ({ ...p, area: e.target.value }))} placeholder="e.g. Restaurant"
                  className="h-11 bg-[#F9F9F8] border-[#E2E8DF]" data-testid="item-area-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm text-[#5C6B64]">Sub Area</Label>
                <Input value={form.sub_area} onChange={(e) => setForm((p) => ({ ...p, sub_area: e.target.value }))} placeholder="e.g. Washroom"
                  className="h-11 bg-[#F9F9F8] border-[#E2E8DF]" data-testid="item-subarea-input" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm text-[#5C6B64]">Task Name <span className="text-[#C45A41]">*</span></Label>
              <Input value={form.task_name} onChange={(e) => setForm((p) => ({ ...p, task_name: e.target.value }))} placeholder="e.g. Floor clean and dry"
                className="h-11 bg-[#F9F9F8] border-[#E2E8DF]" data-testid="item-task-input" />
            </div>
            <div className="flex items-center gap-2">
              <Checkbox checked={form.is_critical}
                        onCheckedChange={(v) => setForm((p) => ({ ...p, is_critical: !!v }))}
                        data-testid="item-critical-check" />
              <Label className="text-sm text-[#5C6B64]">Critical item</Label>
            </div>

            {/* ITER307 · On failure, assign ticket to */}
            <div className="space-y-1.5">
              <Label className="text-sm text-[#5C6B64] flex items-center gap-1.5">
                <Settings2 className="w-3.5 h-3.5" />
                On failure, assign ticket to
              </Label>
              <Select
                value={encodeAssign(form.failure_assign_to)}
                onValueChange={(v) => setForm((p) => ({ ...p, failure_assign_to: decodeAssign(v) }))}
              >
                <SelectTrigger className="h-11 border-[#E2E8DF]" data-testid="item-failure-assign-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="max-h-80">
                  <SelectItem value={NO_OVERRIDE}>Use routing rules (default)</SelectItem>
                  <div className="px-2 pt-2 pb-1 text-[10px] uppercase tracking-wider text-[#5C6B64]">
                    Departments
                  </div>
                  {departments.map((d) => (
                    <SelectItem key={`d-${d}`} value={`${ASSIGN_DEPT_PREFIX}${d}`}
                                data-testid={`assign-dept-${d}`}>
                      {d}
                    </SelectItem>
                  ))}
                  <div className="px-2 pt-2 pb-1 text-[10px] uppercase tracking-wider text-[#5C6B64]">
                    Roles
                  </div>
                  {VALID_ROLES.map((r) => (
                    <SelectItem key={`r-${r}`} value={`${ASSIGN_ROLE_PREFIX}${r}`}
                                data-testid={`assign-role-${r}`}>
                      {roleLabel(r)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-[#5C6B64]">
                Overrides the ticket routing table. Leave as default to keep current behaviour.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="border-[#E2E8DF] text-[#5C6B64]">Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-[color:var(--color-primary)] hover:bg-[color:var(--color-primary-hover)] text-white" data-testid="save-checklist-item-btn">
              {saving ? 'Saving...' : editing ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ITER307 · Bulk failure-assign dialog */}
      <Dialog open={bulkAssignOpen} onOpenChange={setBulkAssignOpen}>
        <DialogContent className="sm:max-w-md" data-testid="bulk-assign-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#1A2421]">Set failure assignment</DialogTitle>
            <DialogDescription className="text-[#5C6B64]">
              Applies to {selectionCount} selected item{selectionCount === 1 ? '' : 's'}.
              Choose &quot;Use routing rules&quot; to clear the override.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label className="text-sm text-[#5C6B64]">On failure, assign ticket to</Label>
            <Select value={bulkAssignVal} onValueChange={setBulkAssignVal}>
              <SelectTrigger className="h-11 border-[#E2E8DF]" data-testid="bulk-assign-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="max-h-80">
                <SelectItem value={NO_OVERRIDE}>Use routing rules (default)</SelectItem>
                <div className="px-2 pt-2 pb-1 text-[10px] uppercase tracking-wider text-[#5C6B64]">
                  Departments
                </div>
                {departments.map((d) => (
                  <SelectItem key={`bd-${d}`} value={`${ASSIGN_DEPT_PREFIX}${d}`}>{d}</SelectItem>
                ))}
                <div className="px-2 pt-2 pb-1 text-[10px] uppercase tracking-wider text-[#5C6B64]">
                  Roles
                </div>
                {VALID_ROLES.map((r) => (
                  <SelectItem key={`br-${r}`} value={`${ASSIGN_ROLE_PREFIX}${r}`}>{roleLabel(r)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkAssignOpen(false)}
                    className="border-[#E2E8DF] text-[#5C6B64]">Cancel</Button>
            <Button onClick={doBulkAssign} disabled={bulkSaving}
                    className="bg-[color:var(--color-primary)] hover:bg-[color:var(--color-primary-hover)] text-white"
                    data-testid="bulk-assign-apply-btn">
              {bulkSaving ? 'Applying…' : 'Apply'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ITER307 · Excel import dialog */}
      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent className="sm:max-w-3xl max-h-[85vh] overflow-y-auto"
                       data-testid="import-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#1A2421]">Bulk import checklist items</DialogTitle>
            <DialogDescription className="text-[#5C6B64]">
              Upload the .xlsx template. Every row is validated + previewed before anything is written.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 py-2">
            <div className="flex flex-wrap items-center gap-2">
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx"
                onChange={onImportFileChange}
                className="text-sm file:mr-3 file:rounded-md file:border file:border-[#E2E8DF]
                           file:bg-white file:px-3 file:py-1.5 file:text-sm
                           file:text-[#1A2421] file:cursor-pointer"
                data-testid="import-file-input"
              />
              <Button size="sm" variant="ghost" onClick={downloadTemplate}
                      className="text-[color:var(--color-primary)]">
                <Download className="w-3.5 h-3.5 mr-1" /> Download blank template
              </Button>
            </div>

            {previewLoading && (
              <div className="text-sm text-[#5C6B64]">Parsing workbook…</div>
            )}

            {importPreview && (
              <div className="space-y-2">
                <div className="grid grid-cols-4 gap-2 text-center">
                  <StatChip label="Total"     value={importPreview.summary.total} />
                  <StatChip label="Valid"     value={importPreview.summary.valid}     tone="ok" />
                  <StatChip label="Duplicate" value={importPreview.summary.duplicate} tone="warn" />
                  <StatChip label="Invalid"   value={importPreview.summary.invalid}   tone="err" />
                </div>

                <div className="border border-[#E2E8DF] rounded-lg overflow-hidden">
                  <div className="max-h-[42vh] overflow-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-[#F9F9F8] sticky top-0">
                        <tr className="text-left text-[#5C6B64]">
                          <th className="px-2 py-1.5">Row</th>
                          <th className="px-2 py-1.5">Status</th>
                          <th className="px-2 py-1.5">Department</th>
                          <th className="px-2 py-1.5">Task Name</th>
                          <th className="px-2 py-1.5">Critical</th>
                          <th className="px-2 py-1.5">Assign To</th>
                          <th className="px-2 py-1.5">Reason</th>
                        </tr>
                      </thead>
                      <tbody data-testid="import-preview-rows">
                        {importPreview.rows.map((r) => (
                          <tr key={r.row}
                              className={`border-t border-[#E2E8DF] ${
                                r.status === 'invalid'   ? 'bg-[#C45A41]/[0.06]' :
                                r.status === 'duplicate' ? 'bg-[#F59E0B]/[0.06]' : ''}`}
                              data-testid={`import-row-${r.row}-${r.status}`}>
                            <td className="px-2 py-1.5 text-[#5C6B64]">{r.row}</td>
                            <td className="px-2 py-1.5">
                              <StatusPill status={r.status} />
                            </td>
                            <td className="px-2 py-1.5 text-[#1A2421]">{r.department}</td>
                            <td className="px-2 py-1.5 text-[#1A2421]">{r.task_name}</td>
                            <td className="px-2 py-1.5">{r.is_critical ? 'Yes' : 'No'}</td>
                            <td className="px-2 py-1.5">
                              {r.failure_assign_to
                                ? (r.failure_assign_to.type === 'role'
                                    ? roleLabel(r.failure_assign_to.value)
                                    : r.failure_assign_to.value)
                                : <span className="text-[#5C6B64]">— rules —</span>}
                            </td>
                            <td className="px-2 py-1.5 text-[#5C6B64]">
                              {r.reason || ''}
                            </td>
                          </tr>
                        ))}
                        {importPreview.rows.length === 0 && (
                          <tr><td colSpan={7} className="text-center py-6 text-[#5C6B64]">
                            No rows found in the workbook.
                          </td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                {importPreview.summary.invalid > 0 && (
                  <div className="flex items-start gap-2 rounded-md bg-[#C45A41]/10 border border-[#C45A41]/30 px-3 py-2 text-xs text-[#7A2E1D]">
                    <AlertTriangle className="w-3.5 h-3.5 mt-0.5" />
                    <span>
                      {importPreview.summary.invalid} row{importPreview.summary.invalid === 1 ? '' : 's'} will be rejected.
                      Fix the file and re-upload, or import only the {importPreview.summary.valid} valid row{importPreview.summary.valid === 1 ? '' : 's'} below.
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setImportOpen(false)}
                    className="border-[#E2E8DF] text-[#5C6B64]">Close</Button>
            <Button
              onClick={commitImport}
              disabled={!importPreview || importing || importPreview.summary.valid === 0}
              className="bg-[color:var(--color-primary)] hover:bg-[color:var(--color-primary-hover)] text-white"
              data-testid="import-commit-btn"
            >
              {importing
                ? 'Importing…'
                : importPreview
                  ? `Import ${importPreview.summary.valid} valid row${importPreview.summary.valid === 1 ? '' : 's'}`
                  : 'Import'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Small stat chip used in the import preview summary row.
function StatChip({ label, value, tone }) {
  const map = {
    ok:   'bg-[color:var(--color-primary)]/10 text-[color:var(--color-primary)]  border-[color:var(--color-primary)]/30',
    warn: 'bg-[#F59E0B]/10 text-[#8A5B00]  border-[#F59E0B]/30',
    err:  'bg-[#C45A41]/10 text-[#7A2E1D]  border-[#C45A41]/30',
  };
  return (
    <div className={`rounded-md border ${map[tone] || 'bg-[#F9F9F8] text-[#1A2421] border-[#E2E8DF]'} py-2`}>
      <div className="text-lg font-semibold">{value}</div>
      <div className="text-[10px] uppercase tracking-wide">{label}</div>
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    valid:     ['Valid',     'bg-[color:var(--color-primary)]/10 text-[color:var(--color-primary)] border-[color:var(--color-primary)]/30'],
    duplicate: ['Duplicate', 'bg-[#F59E0B]/10 text-[#8A5B00] border-[#F59E0B]/30'],
    invalid:   ['Invalid',   'bg-[#C45A41]/10 text-[#7A2E1D] border-[#C45A41]/30'],
  };
  const [label, cls] = map[status] || ['—', ''];
  return (
    <span className={`inline-flex items-center rounded-md border px-1.5 py-0.5 text-[10px] font-semibold ${cls}`}>
      {label}
    </span>
  );
}
